import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Mail,
  Star,
  Paperclip,
  Search,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Inbox,
  Trash2,
  Sparkles,
  KeyRound,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Settings,
  MoreVertical,
  ChevronDown,
  Link2Off,
  Calendar,
  Link,
  Shield,
  X,
  Loader2,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { format, formatDistanceToNow } from "date-fns";
import { useToast } from "@/hooks/use-toast";

interface SyncedEmail {
  id: number;
  emailAccountId: number;
  providerMessageId: string;
  threadId: string | null;
  subject: string | null;
  snippet: string | null;
  fromAddress: string | null;
  fromName: string | null;
  toAddresses: string | null;
  isRead: boolean | null;
  isStarred: boolean | null;
  hasAttachments: boolean | null;
  receivedAt: string;
  analysis?: { emailCategory: string | null; urgency: string | null; requiresAction: boolean | null } | null;
}

interface EmailAccount {
  id: number;
  provider: "gmail" | "outlook";
  email: string;
  lastSyncedAt: string | null;
  isActive: boolean;
}

interface CalendarAccount {
  id: number;
  provider: "google" | "outlook";
  email: string;
  lastSyncedAt: string | null;
  isActive: boolean;
}

export default function EmailInbox() {
  const [, setLocation] = useLocation();
  const [selectedAccountId, setSelectedAccountId] = useState<string>(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("selectedEmailAccountId") || "all";
    }
    return "all";
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [filter, setFilter] = useState<"all" | "unread" | "starred">("all");
  const [selectedEmails, setSelectedEmails] = useState<Set<number>>(new Set());
  const [connectDialogOpen, setConnectDialogOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<
    "gmail" | "outlook" | null
  >(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const pageSize = 25;
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch accounts
  const { data: accountsData, isLoading: accountsLoading } = useQuery<{
    accounts: EmailAccount[];
  }>({
    queryKey: ["/api/email-accounts"],
    queryFn: async () => {
      const response = await fetch("/api/email-accounts");
      if (!response.ok) throw new Error("Failed to fetch accounts");
      return response.json();
    },
  });

  const accounts = accountsData?.accounts || [];

  // Fetch available providers
  const { data: providersData } = useQuery<{ providers: string[] }>({
    queryKey: ["/api/email-accounts/providers"],
    queryFn: async () => {
      const response = await fetch("/api/email-accounts/providers");
      if (!response.ok) throw new Error("Failed to fetch providers");
      return response.json();
    },
  });

  const availableProviders = providersData?.providers || [];

  // Fetch calendar accounts
  const { data: calendarAccountsData } = useQuery<{
    accounts: CalendarAccount[];
  }>({
    queryKey: ["/api/calendar-accounts"],
    queryFn: async () => {
      const response = await fetch("/api/calendar-accounts");
      if (!response.ok) throw new Error("Failed to fetch calendar accounts");
      return response.json();
    },
  });

  const calendarAccounts = calendarAccountsData?.accounts || [];

  // Fetch available calendar providers
  const { data: calendarProvidersData } = useQuery<{ providers: string[] }>({
    queryKey: ["/api/calendar-accounts/providers"],
    queryFn: async () => {
      const response = await fetch("/api/calendar-accounts/providers");
      if (!response.ok) throw new Error("Failed to fetch calendar providers");
      return response.json();
    },
  });

  const availableCalendarProviders = calendarProvidersData?.providers || [];

  // Disconnect email account mutation
  const disconnectMutation = useMutation({
    mutationFn: async (accountId: number) => {
      const response = await fetch(`/api/email-accounts/${accountId}`, {
        method: "DELETE",
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to disconnect");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/email-accounts"] });
      queryClient.invalidateQueries({ queryKey: ["/api/synced-emails"] });
      toast({
        title: "Account disconnected",
        description: "Email account has been removed",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to disconnect",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Disconnect calendar account mutation
  const disconnectCalendarMutation = useMutation({
    mutationFn: async (accountId: number) => {
      const response = await fetch(`/api/calendar-accounts/${accountId}`, {
        method: "DELETE",
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to disconnect");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/calendar-accounts"] });
      queryClient.invalidateQueries({ queryKey: ["/api/synced-calendar-events"] });
      toast({
        title: "Calendar disconnected",
        description: "Calendar account has been removed",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to disconnect",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Sync calendar account mutation
  const syncCalendarMutation = useMutation({
    mutationFn: async (accountId: number) => {
      const response = await fetch(`/api/calendar-accounts/${accountId}/sync`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ maxResults: 100 }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Sync failed");
      }
      return response.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/calendar-accounts"] });
      queryClient.invalidateQueries({ queryKey: ["/api/synced-calendar-events"] });
      toast({
        title: "Calendar Sync Complete",
        description: `Synced ${data.eventsSynced} event${data.eventsSynced !== 1 ? "s" : ""}`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Calendar Sync Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Set default account when accounts load and persist to localStorage
  useEffect(() => {
    if (accounts.length > 0 && selectedAccountId === "all") {
      const newAccountId = accounts[0].id.toString();
      setSelectedAccountId(newAccountId);
      localStorage.setItem("selectedEmailAccountId", newAccountId);
    }
  }, [accounts, selectedAccountId]);

  // Persist selected account to localStorage
  useEffect(() => {
    if (selectedAccountId && selectedAccountId !== "all") {
      localStorage.setItem("selectedEmailAccountId", selectedAccountId);
    }
  }, [selectedAccountId]);

  // Sync mutation
  const syncMutation = useMutation({
    mutationFn: async ({
      accountId,
      maxResults,
      forceFullSync,
    }: {
      accountId: number;
      maxResults: number;
      forceFullSync?: boolean;
    }) => {
      const response = await fetch(`/api/email-accounts/${accountId}/sync`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ maxResults, forceFullSync }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Sync failed");
      }
      return response.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/synced-emails"] });
      queryClient.invalidateQueries({ queryKey: ["/api/email-accounts"] });
      toast({
        title: "Sync Complete",
        description: `Fetched ${data.emailsSynced} new email${data.emailsSynced !== 1 ? "s" : ""}`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Sync Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Delete emails mutation
  const deleteMutation = useMutation({
    mutationFn: async (emailIds: number[]) => {
      const response = await fetch("/api/synced-emails/delete-batch", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ emailIds }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Delete failed");
      }
      return response.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/synced-emails"] });
      setSelectedEmails(new Set());
      toast({
        title: "Emails Deleted",
        description: `Deleted ${data.deletedCount} email${data.deletedCount !== 1 ? "s" : ""}`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Delete Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Batch analysis mutation
  const batchAnalyzeMutation = useMutation({
    mutationFn: async ({
      accountId,
      limit,
    }: {
      accountId: number;
      limit: number | "all";
    }) => {
      const params = limit === "all" ? "?all=true" : `?limit=${limit}`;
      const response = await fetch(
        `/api/email-accounts/${accountId}/analyze-batch${params}`,
        {
          method: "POST",
        }
      );
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Batch analysis failed");
      }
      return response.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["/api/email-accounts", selectedAccountId, "analysis-status"],
      });
      queryClient.invalidateQueries({ queryKey: ["/api/synced-emails"] });
      toast({
        title: "AI Analysis Complete",
        description: data.message,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Analysis Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Analysis status query
  const { data: analysisStatus } = useQuery<{
    total: number;
    analyzed: number;
    unanalyzed: number;
    percentAnalyzed: number;
  }>({
    queryKey: ["/api/email-accounts", selectedAccountId, "analysis-status"],
    queryFn: async () => {
      const response = await fetch(
        `/api/email-accounts/${selectedAccountId}/analysis-status`
      );
      if (!response.ok) throw new Error("Failed to fetch analysis status");
      return response.json();
    },
    enabled: selectedAccountId !== "all",
    // Poll while a batch analysis is running so the progress count moves.
    refetchInterval: batchAnalyzeMutation.isPending ? 2000 : false,
  });

  // Note: Auto-sync removed - sync only on manual click or cache expiry (10 min staleTime)

  // Fetch emails
  const {
    data: emailsData,
    isLoading,
    refetch,
    isFetching,
  } = useQuery<{
    emails: SyncedEmail[];
    total: number;
  }>({
    queryKey: ["/api/synced-emails", selectedAccountId, filter, page, pageSize],
    queryFn: async () => {
      const params = new URLSearchParams({
        limit: pageSize.toString(),
        offset: (page * pageSize).toString(),
      });

      if (selectedAccountId !== "all") {
        params.set("accountId", selectedAccountId);
      }

      if (filter === "unread") {
        params.set("isRead", "false");
      } else if (filter === "starred") {
        params.set("isStarred", "true");
      }

      const response = await fetch(`/api/synced-emails?${params}`);
      if (!response.ok) throw new Error("Failed to fetch emails");
      return response.json();
    },
    enabled: selectedAccountId !== "all" || accounts.length > 0,
    staleTime: 10 * 60 * 1000, // 10 minutes - only refetch after this time
  });

  const emails = emailsData?.emails || [];
  const total = emailsData?.total || 0;
  const totalPages = Math.ceil(total / pageSize);

  const filteredEmails = searchQuery
    ? emails.filter(
        (email) =>
          email.subject?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          email.fromName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          email.fromAddress?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          email.snippet?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : emails;

  const formatEmailDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor(
      (now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (diffDays === 0) {
      return format(date, "h:mm a");
    } else if (diffDays < 7) {
      return format(date, "EEE");
    } else if (date.getFullYear() === now.getFullYear()) {
      return format(date, "MMM d");
    } else {
      return format(date, "MMM d, yyyy");
    }
  };

  const toggleEmailSelection = (emailId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    const newSelected = new Set(selectedEmails);
    if (newSelected.has(emailId)) {
      newSelected.delete(emailId);
    } else {
      newSelected.add(emailId);
    }
    setSelectedEmails(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedEmails.size === filteredEmails.length) {
      setSelectedEmails(new Set());
    } else {
      setSelectedEmails(new Set(filteredEmails.map((e) => e.id)));
    }
  };

  const handleDeleteSelected = () => {
    if (selectedEmails.size === 0) return;
    deleteMutation.mutate(Array.from(selectedEmails));
  };

  const currentAccount = accounts.find(
    (a) => a.id.toString() === selectedAccountId
  );

  // Connect Dialog Component
  const ConnectDialog = () => (
    <Dialog open={connectDialogOpen} onOpenChange={setConnectDialogOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-yellow-500" />
            Connect {selectedProvider === "gmail" ? "Gmail" : "Outlook"} Account
          </DialogTitle>
          <DialogDescription className="space-y-3 pt-2">
            <p>
              You're about to connect your{" "}
              {selectedProvider === "gmail" ? "Google" : "Microsoft"} account to
              sync your emails.
            </p>
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 text-sm text-yellow-800">
              <p className="font-medium mb-1">App Verification Notice</p>
              <p>
                You may see a warning that says "This app isn't verified". This
                is normal while we complete the verification process.
              </p>
              <p className="mt-2">
                Click "Advanced" then "Go to Administr (unsafe)" to continue.
              </p>
            </div>
            <p className="text-sm text-muted-foreground">
              We only request read-only access and will never send emails on
              your behalf.
            </p>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => setConnectDialogOpen(false)}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (selectedProvider) {
                window.location.href = `/api/email-accounts/oauth/${selectedProvider}/start`;
              }
            }}
          >
            <Mail
              className={`w-4 h-4 mr-2 ${selectedProvider === "gmail" ? "text-red-500" : "text-blue-500"}`}
            />
            Continue to {selectedProvider === "gmail" ? "Google" : "Microsoft"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  // No accounts connected - show onboarding screen
  if (!accountsLoading && accounts.length === 0) {
    return (
      <TooltipProvider>
        <div className="space-y-6">
          {/* Onboarding Banner */}
          <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950 dark:to-indigo-950 border-blue-200 dark:border-blue-800">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="rounded-full bg-blue-100 dark:bg-blue-900 p-3">
                  <Mail className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                    Your Email Hub
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-4 max-w-2xl">
                    Connect your email to view client correspondence directly in administr. Your inbox syncs in read-only mode — we never send, delete, or modify your emails. Your data stays private and is only used to help you stay on top of client communications.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
                    <div className="flex items-start gap-2">
                      <Inbox className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Unified Inbox</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">View all client emails alongside your other communications</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <Link className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Client-Linked Emails</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Emails are automatically matched to client records</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <Sparkles className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">AI-Powered Insights</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Get smart summaries and action items from your emails</p>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <Button
                      size="lg"
                      className="bg-blue-600 hover:bg-blue-700 text-white h-auto py-4 px-6"
                      onClick={() => {
                        setSelectedProvider("gmail");
                        setConnectDialogOpen(true);
                      }}
                    >
                      <div className="flex flex-col items-center gap-2">
                        <Mail className="w-8 h-8" />
                        <span>Connect Gmail</span>
                      </div>
                    </Button>

                    <Button
                      size="lg"
                      variant="outline"
                      className="h-auto py-4 px-6"
                      onClick={() => {
                        setSelectedProvider("outlook");
                        setConnectDialogOpen(true);
                      }}
                    >
                      <div className="flex flex-col items-center gap-2">
                        <Mail className="w-8 h-8 text-blue-500" />
                        <span>Connect Outlook</span>
                      </div>
                    </Button>
                  </div>

                  <div className="flex items-center gap-2 text-xs text-muted-foreground mt-4">
                    <Shield className="h-3.5 w-3.5" />
                    <span>Your email data is encrypted and never shared with third parties.</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <ConnectDialog />
        </div>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="text-2xl font-bold">Messages</h1>
            {currentAccount && (
              <Badge variant="outline" className="font-normal">
                {currentAccount.email}
              </Badge>
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* Sync Button */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  disabled={syncMutation.isPending || selectedAccountId === "all"}
                >
                  <RefreshCw
                    className={`w-4 h-4 mr-2 ${syncMutation.isPending ? "animate-spin" : ""}`}
                  />
                  Sync
                  <ChevronDown className="w-4 h-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Sync Options</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() =>
                    syncMutation.mutate({
                      accountId: parseInt(selectedAccountId),
                      maxResults: 25,
                      forceFullSync: false,
                    })
                  }
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Fetch new emails
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() =>
                    syncMutation.mutate({
                      accountId: parseInt(selectedAccountId),
                      maxResults: 100,
                      forceFullSync: true,
                    })
                  }
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Full sync (last 30 days)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* AI Analysis Button */}
            {analysisStatus && (analysisStatus.unanalyzed > 0 || batchAnalyzeMutation.isPending) && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" disabled={batchAnalyzeMutation.isPending}>
                    {batchAnalyzeMutation.isPending ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin text-purple-500" />
                        Analyzing… {analysisStatus.analyzed}/{analysisStatus.total}
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2 text-purple-500" />
                        Analyze
                        <Badge variant="secondary" className="ml-2">
                          {analysisStatus.unanalyzed}
                        </Badge>
                      </>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>AI Analysis</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() =>
                      batchAnalyzeMutation.mutate({
                        accountId: parseInt(selectedAccountId),
                        limit: 10,
                      })
                    }
                    disabled={batchAnalyzeMutation.isPending}
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    Analyze 10 emails
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() =>
                      batchAnalyzeMutation.mutate({
                        accountId: parseInt(selectedAccountId),
                        limit: 25,
                      })
                    }
                    disabled={batchAnalyzeMutation.isPending}
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    Analyze 25 emails
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() =>
                      batchAnalyzeMutation.mutate({
                        accountId: parseInt(selectedAccountId),
                        limit: "all",
                      })
                    }
                    disabled={batchAnalyzeMutation.isPending}
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    Analyze all ({analysisStatus.unanalyzed})
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {/* Settings Sheet */}
            <Sheet open={settingsOpen} onOpenChange={setSettingsOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon">
                  <Settings className="w-4 h-4" />
                </Button>
              </SheetTrigger>
              <SheetContent className="overflow-y-auto">
                <SheetHeader>
                  <SheetTitle>Settings</SheetTitle>
                  <SheetDescription>
                    Manage your connected email and calendar accounts
                  </SheetDescription>
                </SheetHeader>

                <div className="mt-6 space-y-6">
                  {/* Connected Email Accounts */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-medium flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      Email Accounts
                    </h3>
                    {accounts.map((account) => (
                      <div
                        key={account.id}
                        className="flex items-center justify-between p-3 border rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className={`w-8 h-8 rounded-full flex items-center justify-center ${
                              account.provider === "gmail"
                                ? "bg-red-100"
                                : "bg-blue-100"
                            }`}
                          >
                            <Mail
                              className={`w-4 h-4 ${
                                account.provider === "gmail"
                                  ? "text-red-500"
                                  : "text-blue-500"
                              }`}
                            />
                          </div>
                          <div>
                            <div className="text-sm font-medium">
                              {account.email}
                            </div>
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              {account.isActive ? (
                                <>
                                  <CheckCircle2 className="w-3 h-3 text-green-500" />
                                  Connected
                                </>
                              ) : (
                                <>
                                  <AlertTriangle className="w-3 h-3 text-yellow-500" />
                                  Needs reconnection
                                </>
                              )}
                              {account.lastSyncedAt && (
                                <>
                                  <span className="mx-1">·</span>
                                  <Clock className="w-3 h-3" />
                                  {formatDistanceToNow(
                                    new Date(account.lastSyncedAt),
                                    { addSuffix: true }
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        </div>

                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() =>
                                syncMutation.mutate({
                                  accountId: account.id,
                                  maxResults: 25,
                                })
                              }
                            >
                              <RefreshCw className="w-4 h-4 mr-2" />
                              Sync now
                            </DropdownMenuItem>
                            {!account.isActive && (
                              <DropdownMenuItem
                                onClick={() => {
                                  window.location.href = `/api/email-accounts/oauth/${account.provider}/start`;
                                }}
                              >
                                <KeyRound className="w-4 h-4 mr-2" />
                                Reconnect
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-destructive"
                              onClick={() =>
                                disconnectMutation.mutate(account.id)
                              }
                            >
                              <Link2Off className="w-4 h-4 mr-2" />
                              Disconnect
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    ))}
                  </div>

                  {/* Connect New Email Account */}
                  <div className="space-y-3">
                    <h3 className="text-sm font-medium">Add Email Account</h3>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedProvider("gmail");
                          setConnectDialogOpen(true);
                        }}
                      >
                        <Mail className="w-4 h-4 mr-2 text-red-500" />
                        Gmail
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedProvider("outlook");
                          setConnectDialogOpen(true);
                        }}
                      >
                        <Mail className="w-4 h-4 mr-2 text-blue-500" />
                        Outlook
                      </Button>
                    </div>
                  </div>

                  {/* Divider */}
                  <div className="border-t" />

                  {/* Calendar Accounts */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-medium flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      Calendar Accounts
                    </h3>
                    {calendarAccounts.length === 0 ? (
                      <p className="text-sm text-muted-foreground">
                        No calendar accounts connected
                      </p>
                    ) : (
                      calendarAccounts.map((account) => (
                        <div
                          key={account.id}
                          className="flex items-center justify-between p-3 border rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            <div
                              className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                account.provider === "google"
                                  ? "bg-red-100"
                                  : "bg-blue-100"
                              }`}
                            >
                              <Calendar
                                className={`w-4 h-4 ${
                                  account.provider === "google"
                                    ? "text-red-500"
                                    : "text-blue-500"
                                }`}
                              />
                            </div>
                            <div>
                              <div className="text-sm font-medium">
                                {account.email}
                              </div>
                              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                {account.isActive ? (
                                  <>
                                    <CheckCircle2 className="w-3 h-3 text-green-500" />
                                    Connected
                                  </>
                                ) : (
                                  <>
                                    <AlertTriangle className="w-3 h-3 text-yellow-500" />
                                    Needs reconnection
                                  </>
                                )}
                                {account.lastSyncedAt && (
                                  <>
                                    <span className="mx-1">·</span>
                                    <Clock className="w-3 h-3" />
                                    {formatDistanceToNow(
                                      new Date(account.lastSyncedAt),
                                      { addSuffix: true }
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                          </div>

                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() =>
                                  syncCalendarMutation.mutate(account.id)
                                }
                                disabled={syncCalendarMutation.isPending}
                              >
                                <RefreshCw className="w-4 h-4 mr-2" />
                                Sync now
                              </DropdownMenuItem>
                              {!account.isActive && (
                                <DropdownMenuItem
                                  onClick={() => {
                                    window.location.href = `/api/calendar-accounts/${account.id}/reconnect`;
                                  }}
                                >
                                  <KeyRound className="w-4 h-4 mr-2" />
                                  Reconnect
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-destructive"
                                onClick={() =>
                                  disconnectCalendarMutation.mutate(account.id)
                                }
                              >
                                <Link2Off className="w-4 h-4 mr-2" />
                                Disconnect
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      ))
                    )}
                  </div>

                  {/* Connect New Calendar Account */}
                  <div className="space-y-3">
                    <h3 className="text-sm font-medium">Add Calendar Account</h3>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          window.location.href = "/api/calendar-accounts/oauth/google/start";
                        }}
                      >
                        <Calendar className="w-4 h-4 mr-2 text-red-500" />
                        Google Calendar
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          window.location.href = "/api/calendar-accounts/oauth/outlook/start";
                        }}
                      >
                        <Calendar className="w-4 h-4 mr-2 text-blue-500" />
                        Outlook Calendar
                      </Button>
                    </div>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>

        {/* Account Switcher (if multiple accounts) */}
        {accounts.length > 1 && (
          <div className="flex gap-2">
            {accounts.map((account) => (
              <Button
                key={account.id}
                variant={
                  selectedAccountId === account.id.toString()
                    ? "secondary"
                    : "ghost"
                }
                size="sm"
                onClick={() => {
                  setSelectedAccountId(account.id.toString());
                  setPage(0);
                  setSelectedEmails(new Set());
                }}
              >
                <Mail
                  className={`w-4 h-4 mr-2 ${
                    account.provider === "gmail"
                      ? "text-red-500"
                      : "text-blue-500"
                  }`}
                />
                {account.email.split("@")[0]}
              </Button>
            ))}
          </div>
        )}

        {/* Email List Card */}
        <Card>
          {/* Toolbar */}
          <div className="flex items-center gap-3 p-4 border-b">
            {/* Search */}
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search emails..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Filters */}
            <Select
              value={filter}
              onValueChange={(v) => {
                setFilter(v as "all" | "unread" | "starred");
                setPage(0);
              }}
            >
              <SelectTrigger className="w-[120px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="unread">Unread</SelectItem>
                <SelectItem value="starred">Starred</SelectItem>
              </SelectContent>
            </Select>

            {/* Delete button */}
            {selectedEmails.size > 0 && (
              <Button
                variant="destructive"
                size="sm"
                onClick={handleDeleteSelected}
                disabled={deleteMutation.isPending}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete ({selectedEmails.size})
              </Button>
            )}

            {/* Refresh */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => refetch()}
                  disabled={isFetching}
                >
                  <RefreshCw
                    className={`w-4 h-4 ${isFetching ? "animate-spin" : ""}`}
                  />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Refresh list</TooltipContent>
            </Tooltip>
          </div>

          <CardContent className="p-0">
            {isLoading ? (
              <div className="divide-y">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="flex items-center gap-4 p-4">
                    <Skeleton className="w-10 h-10 rounded-full" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-1/3" />
                      <Skeleton className="h-3 w-2/3" />
                    </div>
                    <Skeleton className="h-4 w-16" />
                  </div>
                ))}
              </div>
            ) : filteredEmails.length === 0 ? (
              <div className="text-center py-16">
                <Inbox className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">No Emails</h3>
                <p className="text-muted-foreground text-sm">
                  {filter !== "all"
                    ? `No ${filter} emails`
                    : searchQuery
                      ? "No emails match your search"
                      : "Click Sync to fetch emails from your account"}
                </p>
              </div>
            ) : (
              <>
                {/* Select all header */}
                <div className="flex items-center gap-3 px-4 py-2 border-b bg-muted/30">
                  <Checkbox
                    checked={
                      selectedEmails.size === filteredEmails.length &&
                      filteredEmails.length > 0
                    }
                    onCheckedChange={toggleSelectAll}
                  />
                  <span className="text-sm text-muted-foreground">
                    {selectedEmails.size > 0
                      ? `${selectedEmails.size} selected`
                      : "Select all"}
                  </span>
                </div>

                {/* Email list */}
                <div className="divide-y">
                  {filteredEmails.map((email) => (
                    <div
                      key={email.id}
                      className={`flex items-start gap-3 p-4 hover:bg-muted/50 transition-colors cursor-pointer ${
                        !email.isRead ? "bg-blue-50/50" : ""
                      } ${selectedEmails.has(email.id) ? "bg-muted" : ""}`}
                      onClick={() => setLocation(`/emails/${email.id}`)}
                    >
                      <div
                        onClick={(e) => toggleEmailSelection(email.id, e)}
                        className="pt-1"
                      >
                        <Checkbox
                          checked={selectedEmails.has(email.id)}
                          onCheckedChange={() => {}}
                        />
                      </div>

                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <span className="text-sm font-medium text-primary">
                          {(email.fromName || email.fromAddress || "?")
                            .charAt(0)
                            .toUpperCase()}
                        </span>
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span
                            className={`truncate text-sm ${
                              !email.isRead ? "font-semibold" : "font-medium"
                            }`}
                          >
                            {email.fromName || email.fromAddress || "Unknown"}
                          </span>
                          <span className="text-xs text-muted-foreground flex-shrink-0">
                            {formatEmailDate(email.receivedAt)}
                          </span>
                        </div>
                        <div
                          className={`truncate text-sm ${
                            !email.isRead ? "font-medium" : ""
                          }`}
                        >
                          {email.subject || "(no subject)"}
                        </div>
                        <div className="truncate text-sm text-muted-foreground">
                          {email.snippet || ""}
                        </div>
                      </div>

                      <div className="flex items-center gap-1 flex-shrink-0 pt-1">
                        {email.analysis && (
                          <span title="AI analyzed"><Sparkles className="w-3.5 h-3.5 text-purple-500" /></span>
                        )}
                        {email.analysis?.urgency && ["high", "urgent"].includes(email.analysis.urgency) && (
                          <Badge className="text-[10px] px-1.5 py-0 bg-red-100 text-red-700 hover:bg-red-100">
                            {email.analysis.urgency}
                          </Badge>
                        )}
                        {email.analysis?.emailCategory && (
                          <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                            {email.analysis.emailCategory.replace(/_/g, " ")}
                          </Badge>
                        )}
                        {email.isStarred && (
                          <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                        )}
                        {email.hasAttachments && (
                          <Paperclip className="w-4 h-4 text-muted-foreground" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between p-4 border-t">
                    <p className="text-sm text-muted-foreground">
                      {page * pageSize + 1} -{" "}
                      {Math.min((page + 1) * pageSize, total)} of {total}
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage((p) => Math.max(0, p - 1))}
                        disabled={page === 0}
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setPage((p) => Math.min(totalPages - 1, p + 1))
                        }
                        disabled={page >= totalPages - 1}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        <ConnectDialog />
      </div>
    </TooltipProvider>
  );
}
